import { Container, Grid } from '@mui/material';
import pluralize from 'pluralize';
import React from 'react';

import { formatDateInLocalTimezone } from '~components/business-owner/customers/invoices/utils';
import { useFolded2025Data } from '~components/business-owner/Folded2025/hooks/useFolded2025Data';

import { formatCurrency } from '~utils/functions';

import styles from './Folded2025Customers.module.scss';

import CustomersIcon from '../../assets/Customers_Icon.svg?react';
import CalendarIcon from '../../assets/icons/Calendar.svg?react';
import StarBadgeIcon from '../../assets/icons/StarBadge.svg?react';
import Folded2025Card from '../Folded2025Card';
import Folded2025Title from '../Folded2025Title';

const Folded2025Customers = (): JSX.Element => {
	const { data } = useFolded2025Data();

	return (
		<div className={styles.container}>
			<div className={styles.leftDecoration} />
			<div className={styles.rightDecoration} />
			<Container disableGutters>
				<Folded2025Title icon={<CustomersIcon />}>Customers</Folded2025Title>
				<Grid container spacing={2}>
					<Grid item xs={12} md={6}>
						<Folded2025Card>
							<Folded2025Card.Label>
								You&apos;re so popular! This year you added
							</Folded2025Card.Label>
							<Folded2025Card.Value>
								{data?.summary?.customers?.total_new_customers} new
								customers!
							</Folded2025Card.Value>
						</Folded2025Card>
					</Grid>

					<Grid item xs={12} md={3}>
						<Folded2025Card>
							<Folded2025Card.Label>
								Returning customers
							</Folded2025Card.Label>
							<Folded2025Card.Value>
								{data?.summary?.customers?.percent_returning}%
							</Folded2025Card.Value>
						</Folded2025Card>
					</Grid>

					<Grid item xs={12} md={3}>
						<Folded2025Card>
							<Folded2025Card.Label>New customers</Folded2025Card.Label>
							<Folded2025Card.Value>
								{data?.summary?.customers?.percent_new}%
							</Folded2025Card.Value>
						</Folded2025Card>
					</Grid>

					<Grid item xs={12} md={6}>
						<Folded2025Card icon={<CalendarIcon />}>
							<Folded2025Card.Label>
								Most popular day for customer orders or visits
							</Folded2025Card.Label>
							<Folded2025Card.Value>
								{data?.summary?.customers?.most_popular_days[0] &&
									data?.summary?.customers?.most_popular_days[0]
										.day_of_week}
							</Folded2025Card.Value>
						</Folded2025Card>
					</Grid>

					<Grid item xs={12} md={6}>
						<Folded2025Card>
							<Folded2025Card.Label>
								Total customer lifetime value
							</Folded2025Card.Label>
							<Folded2025Card.Value>
								{formatCurrency(
									data?.summary?.customers
										?.total_customer_lifetime_value,
								)}
							</Folded2025Card.Value>
						</Folded2025Card>
					</Grid>

					<Grid item xs={12}>
						<div className={styles.vipTitle}>Your VIP customers</div>
					</Grid>

					<Grid item xs={12} md={4}>
						<Folded2025Card icon={<StarBadgeIcon />}>
							<Folded2025Card.Label>Most Loyal</Folded2025Card.Label>
							<Folded2025Card.Value>
								{
									data?.summary?.customers?.vip_customers?.most_loyal
										?.name
								}
							</Folded2025Card.Value>
							<Folded2025Card.Label>
								{
									data?.summary?.customers?.vip_customers?.most_loyal
										?.visit_count
								}{' '}
								{pluralize(
									'visit',
									data?.summary?.customers?.vip_customers?.most_loyal
										?.visit_count,
								)}{' '}
								this year
							</Folded2025Card.Label>
						</Folded2025Card>
					</Grid>

					<Grid item xs={12} md={4}>
						<Folded2025Card icon={<StarBadgeIcon />}>
							<Folded2025Card.Label>Highest Spender</Folded2025Card.Label>
							<Folded2025Card.Value>
								{
									data?.summary.customers?.vip_customers
										?.highest_spender.name
								}
							</Folded2025Card.Value>
							<Folded2025Card.Label>
								{formatCurrency(
									data?.summary.customers?.vip_customers
										?.highest_spender?.total_spent,
								)}{' '}
								spent
							</Folded2025Card.Label>
						</Folded2025Card>
					</Grid>

					<Grid item xs={12} md={4}>
						<Folded2025Card icon={<StarBadgeIcon />}>
							<Folded2025Card.Label>Longest Running</Folded2025Card.Label>
							<Folded2025Card.Value>
								{data?.summary.customers?.vip_customers?.day_one?.name}
							</Folded2025Card.Value>
							<Folded2025Card.Label>
								Customer since{' '}
								{formatDateInLocalTimezone(
									data?.summary.customers?.vip_customers?.day_one
										?.customer_since,
								)}
							</Folded2025Card.Label>
						</Folded2025Card>
					</Grid>
				</Grid>
			</Container>
		</div>
	);
};

export default Folded2025Customers;
